import React, { useState } from 'react'
import { Dialog, DialogContent, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddressQualityCard from './AddressQualityCard'
import palette from '../../../theme/palette'

// NETRA quality band -> palette-derived colors for consistency.
const BAND_COLOR = {
  EXCELLENT: palette.success.dark,
  GOOD: palette.info.main,
  FAIR: palette.warning.dark,
  POOR: palette.error.main,
}

/**
 * Returns the palette color for a NETRA address-quality band.
 * Exported so other components (e.g. column renderers) can reuse the same
 * color mapping without duplicating hex values.
 */
export const getNetraBandColor = (band) => BAND_COLOR[band] || palette.grey[600]

/**
 * Compact, color-coded NETRA address-quality score for admin list views.
 * Shows just the score (0–100) in a band-colored pill; band + source on hover.
 * Renders nothing when the order hasn't been scored.
 *
 * Ported from proship-web; the admin panel ALWAYS shows the NETRA score
 * (it ignores the merchant-level showNetraScoreInMerchantPanel flag).
 *
 * When `orderId` is supplied, the pill is clickable and opens a dialog with the
 * full Address Quality card (gauge + sub-score bars + tips) — the same card as
 * the order-detail page, which lazy-fetches its detail from the orderId.
 */
const NetraScoreBadge = ({ netra, orderId, scorePath, scoredStageNote }) => {
  const [open, setOpen] = useState(false)

  if (!netra || netra.score === null || netra.score === undefined) return null
  const score = Math.round(Number(netra.score))
  if (Number.isNaN(score)) return null
  const color = getNetraBandColor(netra.band)
  const clickable = Boolean(orderId)

  const handleOpen = (e) => {
    if (!clickable) return
    e.stopPropagation() // don't trigger row/cell selection
    setOpen(true)
  }
  const handleClose = (e) => {
    e?.stopPropagation?.()
    setOpen(false)
  }

  return (
    <>
      <Tooltip
        title={
          clickable
            ? `Address quality: ${netra.band || '—'} (${score}/100) · click for details`
            : `Address quality: ${netra.band || '—'} (${score}/100) · NETRA`
        }
        arrow
      >
        <span
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (clickable && (e.key === 'Enter' || e.key === ' ')) handleOpen(e)
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 30,
            height: 22,
            padding: '0 8px',
            borderRadius: 11,
            background: color,
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '22px',
            letterSpacing: 0.2,
            cursor: clickable ? 'pointer' : 'default',
          }}
        >
          {score}
        </span>
      </Tooltip>

      {clickable && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth='xs'
          fullWidth
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={handleClose}
            size='small'
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
          <DialogContent sx={{ p: 2 }}>
            {/* Renders only while the dialog is open, so the netra-score
                fetch fires on demand rather than for every list row. */}
            {open && <AddressQualityCard netra={netra} orderId={orderId} scorePath={scorePath} scoredStageNote={scoredStageNote} />}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default NetraScoreBadge
